import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import { useHistory } from "react-router-dom";
import React, { useEffect, useMemo } from "react";
import { BarGraph } from "./componentUtils/BarGraph";
import axios from "../../../../services/axios";
import { ClassExtended, DbUser } from "../../../../interfaces";
import { FeedbackTableView } from "./componentUtils/FeedbackTableView";
import { generateGtmEvent, HandleDataLayer, sanitizeData } from "../../../../Helpers";
import { GridSortModel } from "@mui/x-data-grid";
import { ReportDownloadButton } from "../../utils/ReportDownloadButton";
import { DownloadableReport } from "../../../../hooks/useXLSXGenerator";
import { CourseMetadata } from "@backend-types";

const useStyle = makeStyles((theme) => ({
  root: {},
  backButton: {
    padding: "0px",
    backgroundColor: "transparent",
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    width: "92px",
    height: "45px",
    boxShadow: "none",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      width: "92px",
      height: "45px",
      textDecoration: "underline",
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
}));

interface FeedbackSelectedTargetProps {
  target: string;
  classId: number;
  defaultModuleIds: string[];
  targetName: string;
  classDetails: ClassExtended;
  courseMetadata?: CourseMetadata;
}

export interface TargetFeedback {
  tableData?: TableDataEntity[] | null;
  hintReliance?: HintRelianceEntity[] | null;
}

export interface TableDataEntity {
  questionId: string;
  questionName: string;
  order: number;
  feedbackHintSubmitted: number;
  feedbackHintUnsubmitted: number;
  feedbackErrorSubmitted: number;
  feedbackErrorUnsubmitted: number;
  feedbackHintTotal: number;
  feedbackErrorTotal: number;
}

export interface HintRelianceEntity {
  id: string;
  fullName: string;
  feedbackError: number;
  feedbackHint: number;

  rank: number;
}

const removeMastery = (tableData: TableDataEntity[] | null | undefined) => {
  if (!tableData) return [];
  return tableData.filter((item) => !item.questionId.slice(-2).startsWith("M"));
};

export const FeedbackSelectedTarget = (props: FeedbackSelectedTargetProps) => {
  const classes = useStyle();
  const history = useHistory();
  const { target, classId, targetName, classDetails, courseMetadata } = props;
  const [studentList, setStudentList] = React.useState<DbUser[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [targetFeedbackData, setTargetFeedbackData] = React.useState<TargetFeedback>(
    {} as TargetFeedback
  );
  useEffect(() => {
    if (classId) {
      setLoading(true);
      const getStudentList = async () => {
        try {
          let response = await axios.get(`/insights/class/${classId}`);
          setStudentList(response.data);
        } catch (e) {
          console.log(e);
        }
      };
      getStudentList();
    }
  }, [classId]);

  useEffect(() => {
    const GetTargetFeedbackData = async () => {
      try {
        let response = await axios.post(`/insights/feedback-target`, {
          classIds: [classId],
          courseFilter: {
            targetIds: [target],
          },
          students: studentList,
          v2Feedback: courseMetadata?.insights_page?.use_user_events_v2 ?? false,
        });
        response.data.hintReliance = response.data.hintReliance.sort(
          (a: HintRelianceEntity, b: HintRelianceEntity) => {
            return a.rank - b.rank;
          }
        );
        response.data.hintReliance = response.data.hintReliance.map(
          (item: HintRelianceEntity, index: number) => {
            item.rank = index + 1;
            return item;
          }
        );
        setTargetFeedbackData(response.data);
        setLoading(false);
      } catch (e) {
        console.log(e);
      }
    };
    if (studentList.length > 0) {
      GetTargetFeedbackData();
    } else {
      setLoading(false);
    }
  }, [studentList, target, classId, courseMetadata?.insights_page?.use_user_events_v2]);

  const errorsAcrossProblemsColumns = [
    { field: "questionName", headerName: "Problem", flex: 1 },
    { field: "feedbackErrorSubmitted", headerName: "Submitted", flex: 1 },
    {
      field: "feedbackErrorUnsubmitted",
      headerName: "Unsubmitted",
      flex: 1,
    },
    {
      field: "feedbackErrorTotal",
      headerName: "Total # of errors",
      flex: 1,
    },
  ];

  const hintsAcrossProblemsColumns = [
    { field: "questionName", headerName: "Problem", flex: 1 },
    { field: "feedbackHintSubmitted", headerName: "Submitted", flex: 1 },
    {
      field: "feedbackHintUnsubmitted",
      headerName: "Unsubmitted",
      flex: 1,
    },
    { field: "feedbackHintTotal", headerName: "Total # of hints", flex: 1 },
  ];

  const hintRelianceColumns = [
    {
      field: "rank",
      headerName: "Rank",
      type: "string",
      align: "left",
      maxWidth: 100,
      renderCell: (params: any) => {
        return (
          <Typography
            sx={{
              font: "normal normal normal 14px roboto",
              fontWeight: "bold",
              color: "#5D5D5D",
            }}
            variant="h1">
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "fullName",
      headerName: "Student",
      flex: 1,
    },
    {
      field: "feedbackHint",
      headerName: "Hints",
      flex: 1,
    },
  ];

  const hintsErrorsReport = useMemo<DownloadableReport>(() => {
    const modifiedSheet1 =
      targetFeedbackData.tableData?.map((item) => {
        return sanitizeData({
          Problem: item.questionName,

          "Submitted with Errors": item.feedbackErrorSubmitted,
          "Unsubmitted with Errors": item.feedbackErrorUnsubmitted,
          "Total # Errors": item.feedbackErrorTotal,

          "Submitted with Hints": item.feedbackHintSubmitted,
          "Unsubmitted with Hints": item.feedbackHintUnsubmitted,
          "Total # Hints": item.feedbackHintTotal,
        });
      }) ?? [];

    const modifiedSheet2 =
      targetFeedbackData.hintReliance?.map((item) => {
        return sanitizeData({
          Rank: item.rank,
          Student: item.fullName,
          Hints: item.feedbackHint,
        });
      }) ?? [];

    return {
      fileName: `${classDetails.subject} ${classDetails.period_block} - Hints and Errors - ${targetName} `,
      sheets: [
        {
          name: "Hints & Errors Across Problems",
          header: ["heading1"],
          data: modifiedSheet1,
        },
        {
          name: "Hint Reliance",
          header: ["heading1"],
          data: modifiedSheet2,
        },
      ],
    };
  }, [classDetails.subject, classDetails.period_block, targetFeedbackData, targetName]);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <Stack direction="row" justifyContent="space-between">
          <Button
            size="medium"
            className={classes.backButton}
            variant="contained"
            sx={{ marginLeft: "-0.85em" }}
            onClick={() => {
              HandleDataLayer(
                "link",
                "click",
                "internal link",
                "main content",
                "hints/errors",
                "back",
                "onclick"
              );
              history.push({
                pathname: `/insights/class/${classId}/hints-and-errors`,
              });
            }}
            startIcon={<ArrowBackIcon style={{ width: "16px", height: "16px" }} />}>
            Back
          </Button>
          <ReportDownloadButton report={hintsErrorsReport} />
        </Stack>
        <Typography variant={"h2"} component={"h2"} sx={{ fontWeight: "bold" }}>
          {targetName}
        </Typography>
        <Typography
          sx={{ font: "normal normal 600 20px/24px proxima-soft", color: "#484848" }}>
          Number of hints and errors across problems
        </Typography>
        <Card
          sx={{
            width: "100%",
            height: "100%",
            border: "0px",
            borderRadius: "16px",
            padding: "32px",
            overflow: "visible",
          }}>
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Typography
                component={"span"}
                sx={{ font: "normal normal bold 24px/54px proxima-soft" }}
                variant="body1">
                Errors across problems
              </Typography>
              <BarGraph
                data={targetFeedbackData.tableData!}
                xAxisDataKey={"feedbackErrorTotal"}
                xAxisType={"number"}
                yAxisDataKey={"questionName"}
                yAxisType={"category"}
                stackBar={[
                  {
                    dataKey: "feedbackErrorUnsubmitted",
                    fill: "#FFCCBA",
                  },
                  {
                    dataKey: "feedbackErrorSubmitted",
                    fill: "#FF7F32",
                  },
                ]}
                legend={{
                  feedbackErrorSubmitted: "Submitted",
                  feedbackErrorUnsubmitted: "Unsubmitted",
                }}
              />
              <Typography
                component={"span"}
                sx={{
                  font: "normal normal bold 24px/54px proxima-soft",
                  marginTop: "8px",
                }}
                variant="body1">
                Table View
              </Typography>
              <FeedbackTableView
                rows={targetFeedbackData.tableData ?? []}
                onSortModelChange={(model: GridSortModel) => {
                  const colDef = errorsAcrossProblemsColumns.find(
                    (item) => item.field === model[0].field
                  );
                  const headerName = colDef?.headerName;
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "main content",
                    section: "table view",
                    text: `${headerName ?? model[0].field} ^ sort by`,
                    component: "errors across problems",
                  });
                }}
                columns={errorsAcrossProblemsColumns}
                loading={loading}
              />
            </Box>
          </CardContent>
        </Card>
        <Card
          sx={{
            width: "100%",
            height: "100%",
            border: "0px",
            borderRadius: "16px",
            padding: "32px",
            overflow: "visible",
          }}>
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Typography
                component={"span"}
                sx={{ font: "normal normal bold 24px/54px proxima-soft" }}
                variant="body1">
                Hints across problems
              </Typography>
              <BarGraph
                data={removeMastery(targetFeedbackData.tableData)}
                xAxisDataKey={"feedbackHintTotal"}
                xAxisType={"number"}
                yAxisDataKey={"questionName"}
                yAxisType={"category"}
                stackBar={[
                  {
                    dataKey: "feedbackHintUnsubmitted",
                    fill: "#D4B6DE",
                  },
                  {
                    dataKey: "feedbackHintSubmitted",
                    fill: "#8940A4",
                  },
                ]}
                legend={{
                  feedbackHintSubmitted: "Submitted",
                  feedbackHintUnsubmitted: "Unsubmitted",
                }}
              />
              <Typography
                component={"span"}
                sx={{
                  font: "normal normal bold 24px/54px proxima-soft",
                  marginTop: "8px",
                }}
                variant="body1">
                Table View
              </Typography>
              <FeedbackTableView
                rows={removeMastery(targetFeedbackData.tableData) ?? []}
                columns={hintsAcrossProblemsColumns}
                onSortModelChange={(model: GridSortModel) => {
                  const colDef = hintsAcrossProblemsColumns.find(
                    (item) => item.field === model[0].field
                  );
                  const headerName = colDef?.headerName;
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "main content",
                    section: "hints across problems",
                    text: `${headerName ?? model[0].field} ^ sort by`,
                  });
                }}
                loading={loading}
              />
            </Box>
          </CardContent>
        </Card>
        <Typography
          sx={{
            font: "normal normal 600 20px/24px proxima-soft",
            marginTop: "24px",
            color: "#484848",
          }}>
          Hint reliance
        </Typography>
        <FeedbackTableView
          rows={targetFeedbackData.hintReliance ?? []}
          columns={hintRelianceColumns}
          onSortModelChange={(model: GridSortModel) => {
            const colDef = hintRelianceColumns.find(
              (item) => item.field === model[0].field
            );
            const headerName = colDef?.headerName;
            generateGtmEvent({
              event: "link",
              action: "click",
              name: "onclick",
              type: "internal link",
              region: "main content",
              section: "hint reliance",
              text: `${headerName ?? model[0].field} ^ sort by`,
            });
          }}
          loading={loading}
          border={true}
        />
      </Box>
      {loading && (
        <Backdrop open>
          <CircularProgress color={"primary"} />
        </Backdrop>
      )}
    </>
  );
};
